import { Select } from 'client/components/core/Sidebar/components/Settings/components'
import { StickySetting } from 'client/components/core/Sidebar/components/Settings/entities/SectionSettings/components/StickySetting'
import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DelayOption from 'client/components/core/Sidebar/components/Settings/options/DelayOption'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import ShadowStyle from 'client/components/core/Sidebar/components/Settings/styles/ShadowStyle'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { pageSelectors, usePage } from 'client/store'
import { SectionInterface } from 'common/components/entities/Section/types/SectionInterface'
import { commonWidthLocoKeys } from 'common/constants/commonWidth'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { getMobileBackgroundFileId } from 'common/utils/getMobileBackgroundFileId'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { ImageControl } from 'src/editor/shared/settings/controls/image-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'

interface SectionSettingsProps {
  entity: SectionInterface
}

const SectionSettings = ({ entity }: SectionSettingsProps) => {
  const { isMobile } = useDeviceModeContext()
  const pageType = usePage(pageSelectors.getPageType)
  const updateProp = useUpdateProp(entity)

  const showStickySettings = pageType !== PageTypeEnum.Lecture

  return (
    <div>
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
        withoutHorizontalLine
      />
      <GroupTitle>page_settings.body.background.group_title</GroupTitle>
      <ColorPickerControl
        label="bg color"
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        isClearable
      />
      <ColorPickerControl
        label="inner bg color"
        color={entity.containerBackgroundColor}
        mobileColor={entity.mobileContainerBackgroundColor}
        update={updateProp('containerBackgroundColor')}
        mobileUpdate={updateProp('mobileContainerBackgroundColor')}
        isClearable
      />
      <ImageControl
        isMobile={isMobile}
        backgroundPosition={
          isMobile
            ? entity.mobileBackground || entity.background
            : entity.background
        }
        fileId={
          isMobile
            ? getMobileBackgroundFileId(
                entity.backgroundFileId,
                entity.mobileBackgroundFileId,
              )
            : entity.backgroundFileId
        }
        updateBackgroundFileId={
          isMobile
            ? updateProp('mobileBackgroundFileId')
            : updateProp('backgroundFileId')
        }
        update={
          isMobile ? updateProp('mobileBackground') : updateProp('background')
        }
      />
      {!isMobile && (
        <>
          <GroupTitle>size & position</GroupTitle>
          <Select
            value={entity.contentWidth}
            update={updateProp('contentWidth')}
            labelText="entity_settings.section.content_width.label"
            helpMessage="entity_settings.section.content_width.help_message"
            choiceList={commonWidthLocoKeys}
          />
        </>
      )}
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      {showStickySettings && (
        <StickySetting
          entityHtmlAttrId={entity.htmlAttrId}
          sticky={
            isMobile ? entity.mobileSticky || entity.sticky : entity.sticky
          }
          update={updateProp(isMobile ? 'mobileSticky' : 'sticky')}
        />
      )}
      <DelayOption delay={entity.delay} update={updateProp('delay')} />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </div>
  )
}

export default SectionSettings
